import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gauge, MousePointer2, Smartphone, TrendingUp } from 'lucide-react';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { CTABand } from '@/components/site/CTABand';
import { FAQList } from '@/components/site/FAQ';
import { FaqSchema } from '@/components/site/Seo';
import { HeroImage } from '@/components/site/HeroImage';
import { HERO_IMAGES } from '@/data/heroImages';
import { SITE, SERVICES } from '@/data/site';
import {
  WRAP, SECTION, H2_SM, Accented, SectionHead, PageLinks,
  ProblemsBlock, BuildBlock, ApproachBlock, OutcomesBlock,
} from '@/components/site/ServicePageParts';
import { CITIES } from '@/data/cities';

const PROBLEMS = [
  {
    icon: Gauge,
    title: 'Slow load times',
    body: 'A 1-second delay drops conversions by ~7%. We ship sites that paint in under a second on a midrange phone over LTE.',
  },
  {
    icon: MousePointer2,
    title: 'Outdated design',
    body: 'A 2017 theme says “we stopped caring” louder than any tagline. We design for the way browsers, attention and search look today.',
  },
  {
    icon: Smartphone,
    title: 'Poor mobile experience',
    body: 'Most of your visitors are on a phone in a metro queue. We design mobile-first, then scale up—never the other way around.',
  },
  {
    icon: TrendingUp,
    title: 'Low conversion rates',
    body: 'Pretty without persuasive is decoration. Every section earns its place against one of three goals: trust, clarity, or action.',
  },
];

const WHAT_WE_BUILD = [
  { tag: 'Brand · Trust', title: 'Corporate sites', body: 'Multi-page sites for established companies—leadership, capabilities, investor narrative, careers.', accent: '#6C5CE7' },
  { tag: 'Personal · Craft', title: 'Portfolio sites', body: 'Editorial portfolios for designers, studios and creators. Slow scroll, real typography, no template.', accent: '#4B9FFF' },
  { tag: 'Fundraise · Velocity', title: 'Startup sites', body: 'Product-narrative sites for pre-seed to Series B. Built to ship fast and re-tell the story every quarter.', accent: '#6C5CE7' },
  { tag: 'Performance · Paid', title: 'Landing pages', body: 'High-intent landing pages for ad campaigns and launches. A/B-ready, conversion-instrumented, fast.', accent: '#4B9FFF' },
  { tag: 'Local · Booking', title: 'Service-business sites', body: 'For clinics, studios, agencies, hospitality. Phone-first, WhatsApp-CTA, locale-aware copy.', accent: '#4B9FFF' },
  { tag: 'Editorial · SEO', title: 'Content platforms', body: 'Blogs and resource hubs with proper IA, RSS, OG, and a CMS your marketing team will actually use.', accent: '#6C5CE7' },
];

const PROCESS = [
  { step: '01', title: 'Research', body: 'Stakeholder interviews, competitor teardown, analytics audit. Two weeks of listening before a single rectangle.' },
  { step: '02', title: 'Wireframes', body: 'Low-fi flows in the browser. We argue about hierarchy here, not in pixel-perfect mocks.' },
  { step: '03', title: 'UI design', body: 'One source-of-truth Figma file. Tokens, components, motion notes, real copy.' },
  { step: '04', title: 'Development', body: 'Engineering picks up the file, not a hand-off PDF. React / Next / Astro depending on the brief. CMS wired in week one.' },
  { step: '05', title: 'Testing', body: 'Lighthouse, real-device QA, axe accessibility, content QA with your team. Then we ship—and stay for the first month.' },
];

const TECH = [
  'Next.js', 'React', 'Astro', 'TypeScript', 'Tailwind', 'Framer Motion', 'GSAP', 'Sanity', 'Contentful',
  'Strapi', 'Vercel', 'Cloudflare', 'Supabase', 'Algolia', 'Stripe', 'Shopify', 'Plausible', 'PostHog',
];

const OUTCOMES = [
  { kpi: 98, suffix: '/100', label: 'avg. Lighthouse mobile' },
  { kpi: 42, prefix: '+', suffix: '%', label: 'median conversion lift' },
  { kpi: 3.6, prefix: '×', suffix: '', label: 'mobile session duration' },
  { kpi: 0.9, suffix: 's', label: 'avg. LCP on 4G mid-tier' },
];

const FAQ = [
  { q: 'How long does a website project take?', a: 'A focused marketing site is 6–10 weeks end-to-end. A multi-section corporate site or a heavy content platform runs 10–14 weeks. Landing pages can ship in 2–3 weeks.' },
  { q: 'Do you write the copy or do we?', a: 'Either works. We have a writer on the bench for in-house copy at additional scope. For most engagements your team writes the rough draft and we edit for hierarchy, rhythm and SEO.' },
  { q: 'Which CMS do you recommend?', a: 'Sanity for content-heavy marketing teams, Contentful when there is enterprise IT, Strapi when data must stay self-hosted. We will recommend in week one—not before.' },
  { q: 'Will I be able to edit the site myself?', a: 'Yes. Everything that should be editable is editable. We train your team on day of launch and hand over a Loom + a Notion runbook.' },
  { q: 'Do you handle SEO and analytics?', a: 'We ship a technical SEO foundation (semantic HTML, sitemap, OG, schema, Core Web Vitals) and an analytics layer (Plausible or GA4 + PostHog). Content SEO is a separate engagement.' },
  { q: 'What about hosting?', a: 'Vercel or Cloudflare Pages by default. We can also deploy to your AWS / GCP account. We do not host on a shared box.' },
];

/** Test-id slug used by the shared blocks: "UI design" -> "ui-design". */
const slugify = (s) => s.toLowerCase().replace(/\s+/g, '-');
const PROBLEM_ITEMS = PROBLEMS.map((p) => ({ ...p, slug: slugify(p.title) }));
const BUILD_ITEMS = WHAT_WE_BUILD.map((w) => ({ ...w, slug: slugify(w.title) }));
const PROCESS_STEPS = PROCESS.map((p) => ({ ...p, slug: slugify(p.title) }));
const RELATED = ['web-app-development', 'ecommerce-website-development', 'ai-solutions'];

/* --- Before / After comparison slider --- */
function BeforeAfter() {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current || !wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const p = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setPos(p);
    };
    const onUp = () => { dragging.current = false; document.body.style.userSelect = ''; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  const startDrag = (e) => {
    dragging.current = true;
    document.body.style.userSelect = 'none';
    // also accept the initial click position
    if (wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const p = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setPos(p);
    }
  };

  // Keyboard support
  const onKey = (e) => {
    if (e.key === 'ArrowLeft') setPos((p) => Math.max(0, p - 2));
    if (e.key === 'ArrowRight') setPos((p) => Math.min(100, p + 2));
  };

  const pill = 'pointer-events-none absolute nd-mono !text-[10px] !text-white bg-ink-bg/75 backdrop-blur-sm border border-white/15 rounded-full px-3 py-1.5';

  return (
    <div
      ref={wrapRef}
      data-testid="before-after-slider"
      onMouseDown={startDrag}
      onTouchStart={startDrag}
      className="group relative aspect-[4/3] sm:aspect-[16/10] w-full rounded-[18px] overflow-hidden border border-white/[0.08] bg-ink-surface select-none cursor-ew-resize"
    >
      {/* AFTER (full layer behind) */}
      <MockSite variant="after" />

      {/* BEFORE (clipped) */}
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <MockSite variant="before" />
      </div>

      {/* Labels */}
      <div className={`${pill} top-4 left-4`}>Before</div>
      <div className={`${pill} top-4 right-4`}>After</div>

      {/* Divider + handle */}
      <div
        className="absolute top-0 bottom-0 w-px bg-white"
        style={{ left: `${pos}%` }}
      >
        <button
          type="button"
          data-testid="before-after-handle"
          aria-label="Drag to compare"
          tabIndex={0}
          onKeyDown={onKey}
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-14 w-14 rounded-full bg-white text-ink-bg grid place-items-center transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
        >
          <svg viewBox="0 0 24 16" width="22" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
            <path d="M7 3 2 8l5 5M17 3l5 5-5 5" />
          </svg>
        </button>
      </div>

      {/* progress hint */}
      <div className={`${pill} bottom-4 left-1/2 -translate-x-1/2`}>Drag to compare</div>
    </div>
  );
}

/* Two CSS-only "mock" site frames so the slider is interactive without external assets. */
function MockSite({ variant }) {
  if (variant === 'before') {
    return (
      <div className="absolute inset-0 bg-[#F4F1E8] text-[#1a1a1a]">
        {/* Old-school heavy header */}
        <div className="h-14 border-b border-black/10 px-6 flex items-center justify-between bg-[#E9E4D2]">
          <div className="text-[11px] tracking-widest uppercase">Saraya Bio Solutions Pvt. Ltd.</div>
          <div className="hidden md:flex gap-4 text-[10px] uppercase tracking-widest text-black/70">
            <span>Home</span><span>About Us</span><span>Products</span><span>Services</span><span>Contact</span>
          </div>
        </div>
        <div className="p-6 md:p-10 grid grid-cols-2 gap-6 h-[calc(100%-3.5rem)]">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-orange-700">Welcome to our website</div>
            <div className="mt-3 font-serif text-2xl md:text-4xl leading-[1.15]">
              Leading provider of <span className="text-orange-700">solutions</span> for your business needs since 1998
            </div>
            <p className="mt-3 text-[11px] md:text-[13px] text-black/70 leading-relaxed">
              We are committed to delivering quality products and services to our esteemed clientele nationwide and abroad.
            </p>
            <button className="mt-4 px-4 py-2 bg-orange-700 text-white text-[11px] uppercase tracking-widest">
              Click Here ▸
            </button>
          </div>
          <div className="bg-[length:14px_14px] bg-[linear-gradient(135deg,rgba(0,0,0,0.06)_25%,transparent_25%,transparent_50%,rgba(0,0,0,0.06)_50%,rgba(0,0,0,0.06)_75%,transparent_75%)] border border-black/10 flex items-center justify-center">
            <div className="text-black/40 text-xs uppercase tracking-widest">[ stock image ]</div>
          </div>
        </div>
      </div>
    );
  }
  // After — Ink & Electric style
  return (
    <div className="absolute inset-0 bg-[#0A0A0F] text-white overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
      <div
        className="absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(108,92,231,0.30), transparent 68%)' }}
      />
      <div className="h-14 px-6 flex items-center justify-between border-b border-white/[0.08] bg-black/40 backdrop-blur">
        <div className="flex items-center gap-2 text-[12px] font-semibold tracking-tight">
          <svg viewBox="0 0 32 32" className="h-5 w-5"><path d="M16 5 L28 26 L4 26 Z" fill="none" stroke="#6C5CE7" strokeWidth="2"/></svg>
          Saraya<span className="text-white/50">Bio</span>
        </div>
        <div className="hidden md:flex items-center gap-5 text-[11px] text-white/70">
          <span>Science</span><span>Products</span><span>Press</span><span>Careers</span>
          <span className="px-3 py-1.5 rounded-full bg-violet text-white text-[11px]">Talk to us →</span>
        </div>
      </div>
      <div className="p-6 md:p-10 h-[calc(100%-3.5rem)] grid grid-cols-12 gap-6">
        <div className="col-span-7">
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/50">— Since 1998 · Ohio</div>
          <div className="mt-4 text-2xl md:text-4xl font-extrabold tracking-tight leading-[1.05]">
            Bioscience that <span className="italic text-violet">earns</span> the lab and the boardroom.
          </div>
          <div className="mt-4 text-[12px] text-white/70 leading-relaxed max-w-md">
            Reagents, assays and contract research for US biotech operators. 27 years of compounding precision—now with a digital backbone.
          </div>
          <div className="mt-5 flex gap-2">
            <span className="px-3 py-2 rounded-full bg-violet text-[11px]">Book a lab tour ↗</span>
            <span className="px-3 py-2 rounded-full border border-white/15 text-[11px]">Catalog</span>
          </div>
        </div>
        <div className="col-span-5 grid grid-rows-3 gap-3">
          {[
            { k: '98', s: '/100', l: 'Lighthouse mobile' },
            { k: '+42', s: '%', l: 'demo requests' },
            { k: '0.9', s: 's', l: 'LCP on 4G' },
          ].map((s, i) => (
            <div key={i} className="rounded-xl border border-white/[0.12] bg-white/[0.02] px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-xl font-bold tracking-tight">{s.k}<span className="text-violet">{s.s}</span></div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">{s.l}</div>
              </div>
              <div className="h-8 w-8 rounded-md border border-white/15" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* --- Stack marquee: oversized type, alternate words outlined --- */
function TechMarquee({ items }) {
  const row = items.map((t, i) => (
    <span
      key={t}
      className={`inline-flex items-center gap-[clamp(18px,2.4vw,36px)] pr-[clamp(18px,2.4vw,36px)] font-display font-semibold text-[clamp(36px,5.4vw,84px)] tracking-[-0.045em] leading-[1.1] whitespace-nowrap ${i % 2 ? 'text-transparent [-webkit-text-stroke:1px_rgba(238,238,242,.32)]' : ''}`}
    >
      {t}
      <span aria-hidden className="h-[.16em] w-[.16em] rounded-full" style={{ background: 'var(--nd-grad)' }} />
    </span>
  ));
  return (
    <div className="relative border-y border-white/[0.08] py-[clamp(24px,4vw,48px)] overflow-hidden">
      <div aria-hidden className="absolute inset-y-0 left-0 w-[12vw] z-10 bg-gradient-to-r from-ink-bg to-transparent pointer-events-none" />
      <div aria-hidden className="absolute inset-y-0 right-0 w-[12vw] z-10 bg-gradient-to-l from-ink-bg to-transparent pointer-events-none" />
      <div className="flex w-max">
        <div className="flex items-center shrink-0 [animation:nd-mq_48s_linear_infinite]">{row}</div>
        <div className="flex items-center shrink-0 [animation:nd-mq_48s_linear_infinite]" aria-hidden>{row}</div>
      </div>
    </div>
  );
}

/* --- Page --- */
export default function WebsiteDevelopment() {
  const hero = HERO_IMAGES['website-development'];
  return (
    <div data-testid="website-development-page">
      <FaqSchema items={FAQ} />

      {/* HERO (ServiceHero layout, kept inline for the page's own test ids) */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-[12vw] -top-[18vw] h-[70vw] w-[70vw] max-h-[1000px] max-w-[1000px] rounded-full blur-[20px]"
          style={{ background: 'radial-gradient(circle at 40% 45%, rgba(108,92,231,.32), rgba(75,159,255,.12) 40%, transparent 68%)' }}
        />
        <div className={`relative ${WRAP} pt-16 md:pt-24 pb-20 md:pb-28`}>
          <nav data-testid="breadcrumb" aria-label="Breadcrumb" className="nd-mono flex flex-wrap items-center gap-2.5 !text-[11px]">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span aria-hidden>/</span>
            <Link to="/services/" className="hover:text-white transition-colors">Services</Link>
            <span aria-hidden>/</span>
            <span className="text-white">Website Development</span>
          </nav>

          <SectionLabel as="h1" num="01" data-testid="wd-eyebrow" className="mt-12 md:mt-16">
            {SERVICES[0].h1}
          </SectionLabel>
          <WordReveal
            as="p"
            text="Websites that turn visitors into customers."
            accent="customers."
            data-testid="wd-headline"
            className="mt-8 max-w-[15ch] font-display font-semibold text-[clamp(44px,7.4vw,128px)] leading-[0.92] tracking-[-0.05em]"
          />

          <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-5" data-testid="wd-hero-image">
              <HeroImage src={hero.src} alt={hero.alt} testId="wd-hero-image-frame" />
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal delay={350}>
                <p className="text-lg md:text-xl text-[#C9C9D3] leading-relaxed max-w-[46ch]">
                  We build marketing sites that earn their place in your funnel. Mobile-first, sub-second,
                  measured against three numbers you pick on day one—not against a Dribbble shot.
                </p>
              </Reveal>
              <Reveal delay={450}>
                <div className="mt-10 flex flex-wrap items-center gap-3">
                  <MagneticButton href={SITE.whatsapp} external variant="primary" data-testid="wd-hero-cta-primary">
                    Book discovery call
                  </MagneticButton>
                  <MagneticButton to="/work/" variant="ghost" data-testid="wd-hero-cta-secondary">
                    See website work
                  </MagneticButton>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEMS WE SOLVE */}
      <ProblemsBlock
        heading="The four reasons your site isn’t pulling its weight."
        accentWord="isn’t pulling its weight."
        intro="We've diagnosed all four enough times to know the patterns by week-one. We don't fix all of them on every project—we fix the ones the numbers tell us to."
        items={PROBLEM_ITEMS}
        testIdPrefix="wd"
      />

      {/* WHAT WE BUILD */}
      <BuildBlock
        heading="Six shapes of marketing site."
        accentWord="marketing site."
        intro="Different goals deserve different shapes. We start with what the site has to do—then choose the IA, the page model, the CMS and the motion language around that."
        items={BUILD_ITEMS}
        testIdPrefix="wd"
      />

      {/* DESIGN APPROACH */}
      <ApproachBlock
        heading="Five stages. One file."
        accentWord="One file."
        intro="Research informs wireframes. Wireframes inform UI. UI is built by the engineer who joined in week one. Testing is a stage—not a vibe check."
        steps={PROCESS_STEPS}
        testIdPrefix="wd"
      />

      {/* TECHNOLOGIES — marquee */}
      <section data-testid="wd-tech" className={`${SECTION} overflow-hidden`}>
        <div className={WRAP}>
          <SectionHead
            num="05"
            label="Technologies"
            heading="A stack we'd defend in a code review."
            accent="defend"
            intro="We aren't precious about tools—we are precious about ship-day behaviour. Each of these is here because it makes a site faster, cheaper to run, or easier for your team to live with."
          />
        </div>
        <TechMarquee items={TECH} />
      </section>

      {/* RECENT — BEFORE / AFTER */}
      <section data-testid="wd-before-after" className={SECTION}>
        <div className={WRAP}>
          <SectionHead
            num="06"
            label="Illustration · Before / After"
            heading="A typical rebuild — drag the handle."
            accent="drag the handle."
            intro="An illustrative example, not a client site: an established B2B firm whose website has not kept up. A rebuild like this is planned around three numbers: enquiries, mobile bounce and Core Web Vitals."
          />
          <Reveal>
            <BeforeAfter />
          </Reveal>
        </div>
      </section>

      {/* OUTCOMES */}
      <OutcomesBlock
        heading="Numbers we'd sign a contract against."
        accentWord="a contract"
        intro="Medians across the last 12 website engagements (2024–2025). Individual results vary—what doesn't vary is that we measure and publish them."
        values={OUTCOMES}
        testIdPrefix="wd"
      />

      {/* FAQ */}
      <section data-testid="wd-faq" className={SECTION}>
        <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <SectionLabel num="08">FAQ</SectionLabel>
              <Reveal>
                <h2 className={`mt-7 ${H2_SM}`}><Accented text="Things we're asked before the call." accent="before" /></h2>
              </Reveal>
            </div>
          </div>
          <div className="lg:col-span-8">
            <FAQList items={FAQ} testIdPrefix="wd-faq" />
          </div>
        </div>
      </section>

      <PageLinks
        heading="Website development by city"
        intro="One studio, working across the US. Each state page covers local industries, metros, costs and questions."
        links={[
          ...CITIES.map((c) => ({ to: `${c.path}/`, label: `Website development in ${c.name}` })),
          { to: '/website-development-cost/', label: 'Website development cost' },
          { to: '/services/shopify-development/', label: 'Shopify development' },
          { to: '/services/website-maintenance/', label: 'Website maintenance and redesign' },
          { to: '/work/rupantaram/', label: 'Rupantaram website case study' },
        ]}
        testIdPrefix="wd"
      />

      {/* RELATED SERVICES (RelatedServices layout; this page picks its own three) */}
      <section data-testid="wd-related" className={SECTION}>
        <div className={WRAP}>
          <SectionHead
            num="09"
            label="Related services"
            heading="If this fits, these might too."
            accent="might too."
            aside={<Link to="/services/" className="nd-link-u text-sm text-white whitespace-nowrap">All services →</Link>}
          />
          <ul className="border-t border-white/[0.08]">
            {SERVICES.filter((s) => RELATED.includes(s.slug)).map((s, i) => (
              <Reveal as="li" key={s.slug} delay={i * 60} className="border-b border-white/[0.08]">
                <Link
                  to={`/services/#${s.slug}`}
                  data-testid={`wd-related-${s.slug}`}
                  className="group relative isolate grid grid-cols-[1fr_auto] lg:grid-cols-[220px_1fr_auto] gap-x-8 gap-y-3 items-center py-8 md:py-10"
                >
                  <span aria-hidden className="absolute inset-y-0 -inset-x-4 md:-inset-x-12 -z-10 origin-bottom scale-y-0 bg-[linear-gradient(90deg,rgba(108,92,231,.14),rgba(75,159,255,.04))] transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:scale-y-100" />
                  <span className="nd-mono col-span-2 lg:col-span-1">{s.num} / 06</span>
                  <span className="font-display font-semibold text-[clamp(28px,3.6vw,56px)] tracking-[-0.045em] leading-none transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:translate-x-4">
                    {s.title}
                  </span>
                  <span aria-hidden className="h-12 w-12 md:h-14 md:w-14 rounded-full border border-white/15 grid place-items-center transition-colors duration-300 group-hover:bg-white group-hover:text-ink-bg group-hover:border-white">
                    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" className="transition-transform duration-500 group-hover:-rotate-45"><path d="M2 8h12M9 3l5 5-5 5" /></svg>
                  </span>
                  <p className="col-span-2 lg:col-start-2 lg:col-span-1 text-ink-muted text-[15px] max-w-[60ch]">{s.short}</p>
                </Link>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <CTABand
        eyebrow="Ready to redesign?"
        headline="Let’s ship a site you’ll actually link to."
        sub="A 30-minute WhatsApp call with a partner—no SDR, no deck. Bring the URL of the site you wish yours behaved like."
      />
    </div>
  );
}
